import { catalogServiceClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import type { CatalogEnvelope, ControlAck } from '../types/catalog-service.api.types';
import type {
  FailurePoints,
  ItemRetry,
  RerunAccepted,
  RunRerun,
  RunReset,
  RunRestart,
  RunStageRetry,
} from '../types/recovery.api.types';
import { unwrap } from '../utils/envelope';

const ops = endpoints.catalogService.operations;

/** Each stage's newest attempt, the failure point and what blocks a retry — read from the ledger. */
export async function getFailurePoints(runRef: string): Promise<FailurePoints> {
  const body = await catalogServiceClient
    .get(ops.failurePoints(runRef))
    .json<CatalogEnvelope<FailurePoints>>();
  return unwrap(body);
}

/** Re-open a FAILED/TERMINATED run in place at a stage (defaults to the failure point). */
export async function retryRunStage(runRef: string, json: RunStageRetry): Promise<ControlAck> {
  const body = await catalogServiceClient
    .post(ops.retryStage(runRef), { json })
    .json<CatalogEnvelope<ControlAck>>();
  return unwrap(body);
}

/** Re-run one per-product stage on the SAME run, FAILED products only or FULL. */
export async function retryRunItems(runRef: string, json: ItemRetry): Promise<ControlAck> {
  const body = await catalogServiceClient
    .post(ops.retryItems(runRef), { json })
    .json<CatalogEnvelope<ControlAck>>();
  return unwrap(body);
}

/** Start a NEW run of the whole flow over this run's sync; the ack's `newRunId` names it. */
export async function restartRunFromStage(runRef: string, json: RunRestart): Promise<ControlAck> {
  const body = await catalogServiceClient
    .post(ops.restartFromStage(runRef), { json })
    .json<CatalogEnvelope<ControlAck>>();
  return unwrap(body);
}

/** Reprocess this run's products (FAILED / ALL / chosen ids) as a CHILD run. */
export async function rerunAsChild(runRef: string, json: RunRerun): Promise<RerunAccepted> {
  const body = await catalogServiceClient
    .post(ops.rerun(runRef), { json })
    .json<CatalogEnvelope<RerunAccepted>>();
  return unwrap(body);
}

/** Stop the run and cancel its live execution (no rewind — that is a restart). */
export async function resetRun(runRef: string, json: RunReset): Promise<ControlAck> {
  const body = await catalogServiceClient
    .post(ops.reset(runRef), { json })
    .json<CatalogEnvelope<ControlAck>>();
  return unwrap(body);
}
